import React, { useCallback, useMemo, useContext } from 'react'
import { useIntl } from 'react-intl'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { generateBNBLCheckoutItem } from 'lib/checkout/accommodation/cart'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useAppDispatch } from 'hooks/reduxHooks'
import TextButton from 'LuxKit/Button/TextButton'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getBnblPackageViewsTotalPrice } from 'selectors/offerPage/offerPageSelectors'
import type { BnblTotalPackagesPrices } from 'components/OfferPage/BnblModal/utils'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import Group from 'components/utils/Group'
import { addToCart } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import GeoContext from 'contexts/geoContext'

interface MappedProps {
  bnblModalPackages: Array<App.BnblModal>
  totalRoomsPrice: BnblTotalPackagesPrices
  canViewLuxPlusBenefits: boolean
}

interface Props {
  offer: App.Offer
  isGift: boolean
}

function BnblPackagePricing(props: Props & MappedProps) {
  const {
    bnblModalPackages,
    totalRoomsPrice,
    offer,
    isGift,
    canViewLuxPlusBenefits,
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()

  const { hotelValue, hotelPrice, priceFrom, shouldDisplayValue, hotelMemberPrice, memberPriceFrom } = totalRoomsPrice
  const packageView = bnblModalPackages?.[0].packageView
  const shouldShowValue = hotelValue > 0 && hotelValue > (memberPriceFrom || priceFrom) && shouldDisplayValue
  const valueOffPercent = getValueOffPercent(hotelValue, (!isGift && canViewLuxPlusBenefits ? hotelMemberPrice : hotelPrice))
  const shouldShowRate = valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const onBuyNow = useCallback(() => {
    // TODO: isGift cannot work with multiple cart items
    const checkoutItems = bnblModalPackages.flatMap(modalPackage => generateBNBLCheckoutItem(offer, modalPackage.packageView.package, modalPackage.numberOfRooms))
    dispatch(addToCheckoutCart(checkoutItems, { isGift }))
    Analytics.trackEvent(addToCart())
    dispatch(navigateToCheckout())
  }, [isGift, offer, bnblModalPackages, dispatch])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, {
      price: priceFrom,
      memberPrice: memberPriceFrom,
      packageView,
      skip: isGift,
      currentRegionCode,
      nights: packageView?.package?.duration,
    }),
  ], [offer, packageView, isGift, memberPriceFrom, priceFrom, currentRegionCode])

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
      <VerticalSpacer gap={16}>
        {!isGift && <LoyaltyPointsEarnWithBreakdownModal calculationRequests={pointsEarnCalculationRequests} calculationType="exact" />}
        <div>
          <PriceRowPriceCaption>{intl.formatMessage({
            id: 'nyPCBJvO8OP2gXJr80Qf',
            defaultMessage: 'From',
            description: 'Pricing label prefix before BNBL package total price',
          })}
          </PriceRowPriceCaption>
          <LuxPlusPriceStack
            price={priceFrom}
            memberPrice={!isGift ? memberPriceFrom : 0}
            size="S"
            saleUnit="total"
            offerConfig={offer.luxPlus}
          />
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={hotelValue}
            discountPercentage={shouldShowRate ? valueOffPercent : undefined}
          />}
          <PriceRowTaxesAndFees />
        </div>
      </VerticalSpacer>
      <TextButton data-testid="bnbl-buy-btn" kind="primary" onClick={onBuyNow} size="large">
        {intl.formatMessage({
          id: 'fTb_L_RpGhNq2FcuNUW4',
          defaultMessage: 'Buy now',
          description: 'Button to purchase BNBL package and proceed to checkout',
        })}
      </TextButton>
    </Group>
  )
}

export default withOfferPageAndReduxState((offerPageState, appState: App.State, ownProps: Props): MappedProps => {
  return {
    totalRoomsPrice: getBnblPackageViewsTotalPrice(offerPageState, appState),
    bnblModalPackages: appState.offer.bnblModal[ownProps.offer.id],
    canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(appState),
  }
}, BnblPackagePricing)
